/**
 * The email prefilled into the login form when "Ingat saya" was ticked.
 *
 * Only the email. Never the password, and never anything that could stand in
 * for one: what actually keeps the user signed in is the HttpOnly refresh
 * cookie the server sets, which script on this page cannot read. This file
 * exists so that the person coming back to a shared browser sees their own
 * address already filled in, and nothing more.
 *
 * Every access is wrapped, because localStorage is not reliably available:
 * a private window, a browser configured to block site data, or storage that
 * is simply full all make these calls throw rather than fail quietly.
 */

const KEY = 'sipay.rememberedEmail'

export interface RememberedUser {
  /** The stored email, or '' when there is none. Never throws. */
  read(): string
  remember(email: string): void
  forget(): void
}

/** Storage-shaped, so a test can pass a plain object and a locked-down browser can pass null. */
type KeyValueStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

export function createRememberedUser(storage: KeyValueStore | null): RememberedUser {
  return {
    read() {
      try {
        return storage?.getItem(KEY)?.trim() ?? ''
      } catch {
        return ''
      }
    },
    remember(email: string) {
      const trimmed = email.trim()
      try {
        // An empty value would prefill the field with nothing while still
        // claiming someone is remembered, so treat it as forgetting instead.
        if (trimmed) storage?.setItem(KEY, trimmed)
        else storage?.removeItem(KEY)
      } catch {
        // Not being able to remember an email is not worth failing a login over.
      }
    },
    forget() {
      try {
        storage?.removeItem(KEY)
      } catch {
        /* see above */
      }
    },
  }
}

function browserStorage(): KeyValueStore | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    // Merely touching the accessor throws when site data is blocked.
    return null
  }
}

export const rememberedUser = createRememberedUser(browserStorage())
